import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, RotateCcw, Megaphone, Lightbulb, Sparkles, Settings, Trophy } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { apiRequest, queryClient, getQueryFn } from "@/lib/queryClient";
import { WIDGET_REGISTRY } from "@/lib/widget-registry";
import { WidgetShell } from "@/components/widget-shell";
import { AddWidgetDialog } from "@/components/add-widget-dialog";
import { EditWidgetDialog } from "@/components/edit-widget-dialog";
import { UpdatesFeedDialog } from "@/components/updates-feed-dialog";
import HabitCompletionAlert from "@/components/habit-completion-alert";
import TourLauncher from "@/components/tour-launcher";
import { ScorecardDialog } from "@/components/scorecard-dialog";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import type { Habit, HabitEntry } from "@shared/schema";

type DashboardWidget = {
  id: string;
  tenantId: string;
  userId: string;
  widgetType: string;
  title: string | null;
  size: 'full' | 'half' | 'quarter';
  orderIndex: number;
  column: number;
  isVisible: boolean;
  settings: Record<string, any>;
  createdAt: Date | string;
  updatedAt: Date | string;
};

function SortableWidget({ 
  widget, 
  onEdit, 
  onDelete 
}: { 
  widget: DashboardWidget; 
  onEdit: () => void; 
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: widget.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  
  const config = WIDGET_REGISTRY[widget.widgetType];
  if (!config) return null;
  
  const WidgetComponent = config.component;
  
  const sizeClasses = 
    widget.size === 'full' ? "col-span-12" :
    widget.size === 'half' ? "col-span-12 md:col-span-6" :
    "col-span-12 md:col-span-6 lg:col-span-3";
  
  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...attributes} 
      {...listeners}
      className={sizeClasses}
    >
      <WidgetShell
        widgetId={widget.id}
        widgetType={widget.widgetType}
        title={widget.title}
        size={widget.size}
        onEdit={onEdit}
        onDelete={onDelete}
        isDragging={isDragging}
      >
        <WidgetComponent widgetId={widget.id} {...widget.settings} />
      </WidgetShell>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [isAddWidgetOpen, setIsAddWidgetOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState<DashboardWidget | null>(null);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [widgetToDelete, setWidgetToDelete] = useState<string | null>(null);
  const [isUpdatesFeedOpen, setIsUpdatesFeedOpen] = useState(false);
  const [scorecardOpen, setScorecardOpen] = useState(false);
  const [habitsNearCompletion, setHabitsNearCompletion] = useState<(Habit & { completedCount: number })[]>([]);
  const [showHabitAlert, setShowHabitAlert] = useState(false);

  const { data: widgets = [], isLoading } = useQuery<DashboardWidget[]>({
    queryKey: ["/api/dashboard/widgets"],
  });

  const { data: habits = [] } = useQuery<Habit[]>({
    queryKey: ["/api/habits"],
  });

  const { data: userPreferences } = useQuery<{ dismissedHabitIds?: string[] }>({
    queryKey: ["/api/user/preferences"],
  });

  // Check for habits nearing completion when dashboard loads
  useEffect(() => {
    const checkHabitsNearCompletion = async () => {
      if (!habits.length || !userPreferences) return;

      const dismissedHabitIds = userPreferences.dismissedHabitIds || [];
      const habitsToCheck: (Habit & { completedCount: number })[] = [];

      for (const habit of habits) {
        // Skip if habit was dismissed
        if (dismissedHabitIds.includes(habit.id)) continue;

        // Skip Sequence Successing habits
        if (habit.sequenceSuccessing) continue;

        // Skip Habit Stack habits unless they reached phase 3 (manual target input)
        if (habit.isHabitStack && (habit.habitStackPhase ?? 0) < 3) continue;

        // Fetch habit entries to count completed
        try {
          const response = await fetch(`/api/habits/${habit.id}/entries?period=${habit.frequency}`);
          if (!response.ok) continue;
          
          const entries: HabitEntry[] = await response.json();
          const completedCount = entries.filter(e => e.completed).length;
          const remaining = habit.target - completedCount;

          // If 3 or fewer checkboxes remaining, add to alert list
          if (remaining > 0 && remaining <= 3) {
            habitsToCheck.push({ ...habit, completedCount });
          }
        } catch (error) {
          console.error(`Failed to check habit ${habit.id}:`, error);
        }
      }

      // Always update state to clear alert when no habits qualify
      setHabitsNearCompletion(habitsToCheck);
      setShowHabitAlert(habitsToCheck.length > 0);
    };

    checkHabitsNearCompletion();
  }, [habits, userPreferences]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const deleteWidgetMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/dashboard/widgets/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/widgets"] });
      setWidgetToDelete(null);
    },
    onError: () => {
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (updates: Array<{id: string; orderIndex: number; column: number}>) => {
      await apiRequest("POST", "/api/dashboard/widgets/reorder", { widgets: updates });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/widgets"] });
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/dashboard/widgets/reset");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/widgets"] });
      setIsResetDialogOpen(false);
    },
    onError: () => {
    },
  });

  const sortedWidgets = [...widgets].sort((a, b) => a.orderIndex - b.orderIndex);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = sortedWidgets.findIndex(w => w.id === active.id);
      const newIndex = sortedWidgets.findIndex(w => w.id === over.id);
      const reordered = arrayMove(sortedWidgets, oldIndex, newIndex);
      const updates = reordered.map((w, idx) => ({
        id: w.id,
        orderIndex: idx,
        column: w.column,
      }));
      reorderMutation.mutate(updates);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="bg-card border-b border-border px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-foreground truncate">
              Welcome back{user?.firstName ? `, ${user.firstName}` : ''}! 👋
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              Customize your dashboard by adding, editing, and arranging widgets.
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
            <Link href="/suggestions" className="flex-shrink-0">
              <Button
                variant="outline"
                size="icon"
                data-testid="button-suggestions"
                title="Community Suggestions"
                className="h-9 w-9"
              >
                <Lightbulb className="w-4 h-4 text-yellow-500" />
              </Button>
            </Link>
            <Link href="/tours" className="flex-shrink-0">
              <Button
                variant="outline"
                size="icon"
                data-testid="button-tours"
                title="Tours"
                className="h-9 w-9"
              >
                <Sparkles className="w-4 h-4 text-purple-500" />
              </Button>
            </Link>
            <TourLauncher />
            <Button
              variant="outline"
              size="icon"
              onClick={() => setIsUpdatesFeedOpen(true)}
              data-testid="button-updates-feed"
              className="relative h-9 w-9 flex-shrink-0"
              title="Updates & Announcements"
            >
              <Megaphone className="w-4 h-4 text-red-500" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setIsResetDialogOpen(true)}
              data-testid="button-reset-layout"
              className="hidden sm:inline-flex h-9 w-9"
              title="Reset Layout"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setScorecardOpen(true)}
              data-testid="button-scorecard"
              title="Performance Scorecard"
              className="h-9 w-9 flex-shrink-0 text-amber-500 hover:text-amber-600"
            >
              <Trophy className="w-4 h-4" />
            </Button>
            <Link href="/settings" className="flex-shrink-0">
              <Button
                variant="outline"
                size="icon"
                data-testid="button-admin-panel-dashboard"
                title="Settings"
                className="h-9 w-9"
              >
                <Settings className="w-4 h-4" />
              </Button>
            </Link>
            <Button
              onClick={() => setIsAddWidgetOpen(true)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground flex-1 sm:flex-initial"
              data-testid="button-add-widget"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Widget
            </Button>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {/* Widgets Grid */}
        {sortedWidgets.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">No widgets yet</h3>
            <p className="text-muted-foreground mb-4">
              Start customizing your dashboard by adding widgets
            </p>
            <Button onClick={() => setIsAddWidgetOpen(true)} data-testid="button-add-first-widget">
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Widget
            </Button>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sortedWidgets.map(w => w.id)} strategy={verticalListSortingStrategy}>
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-12 gap-4 md:gap-6">
                {sortedWidgets.map((widget) => (
                  <SortableWidget
                    key={widget.id}
                    widget={widget}
                    onEdit={() => setEditingWidget(widget)}
                    onDelete={() => setWidgetToDelete(widget.id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Add Widget Dialog */}
      <AddWidgetDialog
        isOpen={isAddWidgetOpen}
        onClose={() => setIsAddWidgetOpen(false)}
      />

      {/* Edit Widget Dialog */}
      <EditWidgetDialog
        widget={editingWidget}
        isOpen={!!editingWidget}
        onClose={() => setEditingWidget(null)}
      />

      {/* Updates Feed Dialog */}
      <UpdatesFeedDialog
        open={isUpdatesFeedOpen}
        onOpenChange={setIsUpdatesFeedOpen}
      />

      {/* Scorecard Dialog */}
      <ScorecardDialog open={scorecardOpen} onOpenChange={setScorecardOpen} />

      {/* Habit Completion Alert */}
      <HabitCompletionAlert
        habits={habitsNearCompletion}
        open={showHabitAlert}
        onOpenChange={setShowHabitAlert}
      />

      {/* Reset Confirmation Dialog */}
      <AlertDialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <AlertDialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Dashboard Layout?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove all your custom widgets and restore the default dashboard layout. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => resetMutation.mutate()}
              disabled={resetMutation.isPending}
              data-testid="button-confirm-reset"
            >
              {resetMutation.isPending ? "Resetting..." : "Reset Dashboard"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Widget Confirmation Dialog */}
      <AlertDialog open={!!widgetToDelete} onOpenChange={() => setWidgetToDelete(null)}>
        <AlertDialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Widget?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this widget from your dashboard? You can add it back later from the widget library.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => widgetToDelete && deleteWidgetMutation.mutate(widgetToDelete)}
              disabled={deleteWidgetMutation.isPending}
              data-testid="button-confirm-delete-widget"
            >
              {deleteWidgetMutation.isPending ? "Removing..." : "Remove Widget"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
